import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PlusIcon } from "@heroicons/react/20/solid";

import AddressForm from "../../components/deliveryLoctions/AddressForm";
import AddressCard1 from "../../components/deliveryLoctions/AddressCard1";
import { getDeliveryLocationsV2 } from "../../utils/zulalWebAPI";

const AddressBook = () => {
  const [formMode, setFormMode] = useState(null); // 'add' | 'edit' | null
  const [selectedAddress, setSelectedAddress] = useState(null);

  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAddresses = async () => {
    try {
      setIsLoading(true);
      const response = await getDeliveryLocationsV2();
      setAddresses(response?.data || []);
    } catch (err) {
      console.error("Failed to fetch delivery locations:", err);
      setAddresses([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch addresses when component mounts and whenever formMode changes
    // (close form after save/update triggers refresh via formMode reset)
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMode]);

  const windowScrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const handleAdd = () => {
    setSelectedAddress(null);
    setFormMode("add");
    windowScrollToTop();
  };

  const handleEdit = (address) => {
    // Only open the form. Do NOT reference formData here.
    setSelectedAddress(address);
    setFormMode("edit");
    windowScrollToTop();
  };

  const handleCloseForm = () => {
    setFormMode(null);
    setSelectedAddress(null);
    windowScrollToTop();
  };

  // Called by AddressCard1 after delete / set default etc.
  const refreshList = async () => {
    await fetchAddresses();
    setFormMode(null);
    windowScrollToTop();
  };

  return (
    <div className="mx-auto mr-4 ml-4 mb-28">
      <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
        Your Addresses
        <p className="text-darkTextColor font-poppins text-C12">
          Create / Update / Remove your addresses
        </p>
      </div>

      {/* Address Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
        {/* Add Address Box */}
        <div
          onClick={handleAdd}
          className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-md h-full
                     shadow-md cursor-pointer hover:bg-gray-50 transition"
        >
          <div className="text-center">
            <div className="text-xl text-gray-400 mb-2">
              <PlusIcon className="h-8 w-8 mx-auto" />
              <div className="text-lg text-gray-500 font-medium">
                <button className="bg-blue-600 text-white px-4 py-2 rounded mb-4">
                  Add Address
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading state (optional) */}
        {isLoading ? (
          <div className="text-center">
            <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-md h-full shadow-md">
              <div className="text-lg text-gray-500 font-medium p-4">
                Loading...
              </div>
            </div>
          </div>
        ) : addresses.length === 0 ? (
          <div className="text-center">
            <div
              className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-md h-full
                         shadow-md cursor-pointer hover:bg-gray-50 transition"
            >
              <div className="text-lg text-gray-500 font-medium">
                <p className="bg-blue-600 text-white px-4 py-2 rounded mb-4">
                  No Delivery Location Found
                </p>
              </div>
            </div>
          </div>
        ) : (
          addresses.map((address) => (
            <AddressCard1
              key={address.locationId}
              address={address}
              onEdit={handleEdit}
              refreshList={refreshList}
              totalLocations={addresses.length}
            />
          ))
        )}
      </div>

      {/* add and edit form */}
      <div>
        <AnimatePresence>
          {formMode && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-6"
            >
              <AddressForm
                mode={formMode}
                address={selectedAddress}
                onClose={handleCloseForm}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AddressBook;
